<script>
import PossibleWords from './components/PossibleWords.svelte';
import Hint from './components/Hint.svelte';
import Anagram from './components/Anagram.svelte';
import Stat from './components/Stat.svelte';
import { pickAnagram } from "./js/pick-anagram.js";
import { onMount } from 'svelte';
import { randomItemFromArray } from "./js/utils.js";

// Game state
let level = 0;
let points = 0;
let movesLeft = 0;
let hints = 0;
let wordsFound = [];
let possibleWords = [];
let jumble = '';
let key = '';
let lastWord = '';
let hintWord = null;
let hint = null;
let jumblesUsed = [];
let status = null;
let failure = false;
let success = false;

const missingWords = () => {
	return possibleWords.filter(word => !wordsFound.includes(word));
}

onMount(async () => {
	newGame();
});

// Methods
function loadLevel() {
	level++;
	wordsFound = [];
	hintWord = null;
	hint = null;
	status = level === 1 ? 'Rearrange the letters to spell words.' : null;
	failure = false;
	success = false;

	({jumble, possibleWords, key} = pickAnagram(3 + level, jumblesUsed));
	lastWord = jumble;
	jumblesUsed = [key, ...jumblesUsed]
}

function newGame() {
	level = 0;
	points = 0;
	movesLeft = 5;
	hints = 1;
	jumblesUsed = [];
	loadLevel();
}

function useHint() {
	hints--;
	hintWord = randomItemFromArray(missingWords());
	hint = randomItemFromArray(hintWord.meanings);
}

function checkForMatch(word) {	
	if(word === lastWord) return;
	lastWord = word;

	movesLeft--;

  const match = possibleWords.find(w => w.name === word);
  const wordAlreadyUsed = wordsFound.find(w => w.name === word);

	if(wordAlreadyUsed) {
		status = 'You already found this word!';
		return;
	} else if (match) {
    wordFound(match, match === hintWord);
		return;
  } 
	
	if (movesLeft === 0) {
		lose();
		return;
  }

	status = null;
	return;
}

function wordFound(word, isMatch) {
	points++;
	movesLeft += 3;
	wordsFound = [...wordsFound, word];
	success = true;

	if(isMatch) {
		hintWord = null;
		hint = null;
		status = `You found the hint: &ldquo;${word.name}&rdquo;`;
	} else {
		status = `You found a word: &ldquo;${word.name}&rdquo;`
	}
	
	if (wordsFound.length === possibleWords.length) {
		status = "You found all the words and unlocked a hint!";
		hints++;
		return;
	}

	setTimeout(() => { success = false }, 500);
	return;
}

function lose() {
	const missedWords = missingWords().map(w => `&ldquo;${w.name}&rdquo;`).join(', ');
	status = `You lose! You missed ${missedWords}.`;
	failure = true;
	return;
}
</script>

<main>
	<div class="top-ui">
		<div class="possible-words">
			<PossibleWords {possibleWords} {wordsFound}/>
		</div>

		<div class="level-points">
			<Stat label="Level" value={level} />
			<Stat label="Points" value={points} />
		</div>
	</div>

	<div class="hint">
		{#if hint }
			<Hint {hint} />
		{/if}
	</div>

	<div class="jumble">
		<Anagram {jumble} {success} {failure} on:sort={(e) => { checkForMatch(e.detail.word) }} />
	</div>

	<div class="status">
		{#if status}
			<p>{@html status}</p>
		{/if}
	</div>

	<div class="action">
		{#if failure}
			<button
				class="button"
				on:click={newGame}
			>
				New Game
			</button>
		{:else if wordsFound.length > 0}
			<button
				class="button"
				on:click={loadLevel}
			>
				Next Level
			</button>
		{/if}
	</div>
	
	<div class="bottom-ui">
		<div class="hint-count">
			<Stat label="Hints" value={hints} />
		</div>

		{#if hints > 0 && wordsFound.length < possibleWords.length}
			<button
				class="button button--small"
				on:click={useHint}
			>
				Use a Hint
			</button>
		{/if}

		<div class="moves-left">
			<Stat label="Moves" value={movesLeft} />
		</div>
	</div>
</main>

<style>
	main {
		justify-content: space-between;
		display: flex;
		flex-direction: column;
  	padding: var(--app-padding);
		width: 100%;
  	height: 100%;
	}

	main > * + * {
		margin-top: 1em;
	}

	.top-ui {
		display: flex;
		justify-content: space-between;
	}

	.level-points {
		display: flex;
		flex-wrap: wrap;
		justify-content: flex-end;
		gap: 0.5em;
	}

	.possible-words {
		display: flex;
		flex-direction: column;
		justify-content: flex-start;
	}

	.hint {
		display: grid;
		place-items: center;
		min-height: 5em;
	}

	.jumble {
		display: grid;
		place-items: center;
	}
	
	.status {
		display: flex;
		text-align: center;
		justify-content: center;
		align-items: start;
		min-height: 1.5em;
	}

	.action {
		display: flex;
		justify-content: center;
	}

	.bottom-ui {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-top: auto;
	}

	.hint-count {
		grid-area: hint-count;
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		align-items: flex-end;
	}

	.moves-left {
		grid-area: moves-left;
		display: flex;
		align-items: flex-end;
		justify-content: flex-start;
	}
</style>